import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { getMyWork, getLeaveBalances, getScreenshots } from '../api';
import { getInitials, scoreColor } from '../utils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const shotUrl = (f) => !f ? '' : (f.startsWith('http') ? f : f.startsWith('/') ? `${API_BASE}${f}` : `${API_BASE}/screenshots/${f}`);
const fmtHM = (s) => { s = Math.max(0, s || 0); return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`; };
const dayLabel = (d) => new Date(d + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' });

function Stat({ label, value, color }) {
  return (
    <div style={{ background: 'var(--glass-1, #f8fafc)', border: '1px solid var(--hairline)', borderRadius: 10, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || 'var(--text)' }}>{value}</div>
    </div>
  );
}

// Work-summary "profile" for a team member — opened from Team View.
export default function EmployeeProfile({ emp, onClose }) {
  const e = emp.employee;
  const [work, setWork] = useState(null);
  const [balances, setBalances] = useState([]);
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [w, b, s] = await Promise.all([
        getMyWork(e.id, 'week').catch(() => null),
        getLeaveBalances(e.id).catch(() => []),
        getScreenshots({ employee_id: e.id, limit: 6 }).catch(() => []),
      ]);
      if (cancelled) return;
      setWork(w); setBalances(b || []); setShots((s || []).slice(0, 6)); setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [e.id]);

  const status = emp.is_active ? { t: 'Active', c: '#16a34a', bg: '#dcfce7' } : emp.on_break ? { t: 'On Break', c: '#b45309', bg: '#fef3c7' } : { t: 'Offline', c: '#64748b', bg: 'var(--hairline)' };
  const chartData = (work?.activities || []).map(a => ({ name: dayLabel(a.date), hours: +(a.seconds / 3600).toFixed(2) }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720, width: '92vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={ev => ev.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <div className="employee-avatar" style={{ width: 52, height: 52, fontSize: '1.1rem' }}>{getInitials(e.name)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: '1.15rem' }}>{e.name}</div>
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>{e.department || '—'}</div>
          </div>
          <span style={{ background: status.bg, color: status.c, padding: '4px 12px', borderRadius: 999, fontWeight: 700, fontSize: 12 }}>● {status.t}</span>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: 'var(--muted)', lineHeight: 1 }}>×</button>
        </div>

        {loading ? <div style={{ color: 'var(--muted)', padding: 24, textAlign: 'center' }}>Loading summary…</div> : (
          <>
            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 16 }}>
              <Stat label="This Week" value={fmtHM(work?.worked_seconds)} />
              <Stat label="Active" value={fmtHM(work?.active_seconds)} />
              <Stat label="Idle" value={`${work?.idle_pct || 0}%`} color={work?.idle_pct > 30 ? '#d97706' : undefined} />
              <Stat label="Productivity" value={work?.productive_pct == null ? '—' : `${work.productive_pct}%`} color={work?.productive_pct != null ? scoreColor(work.productive_pct) : undefined} />
              <Stat label="Sessions" value={emp.sessions ?? '—'} />
            </div>

            {/* Today's clock in/out */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 18, fontSize: 13 }}>
              <div><span className="text-muted">Today in: </span><strong>{work?.clock_in ? new Date(work.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</strong></div>
              <div><span className="text-muted">Today out: </span><strong>{work?.clock_out ? new Date(work.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</strong></div>
            </div>

            {/* Weekly worked chart */}
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Worked this week</div>
            {chartData.length === 0 ? <div className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>No sessions this week.</div> : (
              <div style={{ height: 160, marginBottom: 18 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--hairline)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v}h`, 'Worked']} cursor={{ fill: 'var(--hairline)' }} />
                    <Bar dataKey="hours" fill="var(--primary, #6d5efc)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Leave balances */}
            {balances.length > 0 && (
              <>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Leave balances</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
                  {balances.map(b => (
                    <div key={b.id} style={{ border: '1px solid var(--hairline)', borderRadius: 8, padding: '6px 12px', fontSize: 13 }}>
                      <span style={{ fontWeight: 600 }}>{b.name}:</span> {b.unlimited ? '∞' : `${b.remaining}/${b.allowed}`}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Recent screenshots */}
            {shots.length > 0 && (
              <>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Recent screenshots</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 8 }}>
                  {shots.map(ss => (
                    <div key={ss.id} style={{ border: '1px solid var(--hairline)', borderRadius: 8, overflow: 'hidden' }}>
                      <img src={shotUrl(ss.filename)} alt="" style={{ width: '100%', height: 70, objectFit: 'cover', display: 'block', background: 'var(--hairline)' }} onError={ev => { ev.target.style.display = 'none'; }} />
                      <div style={{ fontSize: 10, color: 'var(--muted)', padding: '2px 5px' }}>{new Date(ss.captured_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}{ss.app_name ? ` · ${ss.app_name}` : ''}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
